import { assert, log } from '../app/logger.js';
import { UILoader } from './ui_loader.js';
import { AsapPromise } from '../util/asap_promise.js';
import { window } from '../window.js';

/**
 * Converts static |selectors| property (which returns an object representing
 * { name, selector } pairs, see example below) into a static |selectors| getter.
 * Also converts |elements| property presented by { name, selector} pairs into
 * element getters. If element name is ended by 's' then it converted into
 * querySelectorAll().
 *
 * For example:
 *
 * class A extends UI {
 *   static get selectors() {
 *     const itemsel = '.item';
 *     return {
 *       AllItems: itemsel,
 *       SelectedItem: `${itemsel}.selected-item`,
 *     }
 *   }
 *
 *   static elements = {
 *     allItemElements: this.selectors.AllItems,
 *     selectedItemElement: this.selectors.SelectedItem,
 *     itemAt(i) { return `${this.selectors.AllItems}:nth-of-type(${i})}
 *   };
 * }
 *
 * will be converted into:
 *
 * class A extends UI {
 *   static get selectors = {
 *     AllItems: '.item',
 *     SelectedItem: '.item.selected-item',
 *   };
 *   get allItemElements() {
 *     return this.querySelectorAll('.item');
 *   }
 *   get selectedItemElement() {
 *     return this.querySelector('.item.selected-item');
 *   }
 *   itemAt(i) { return this.querySelector(itemAt(i)); }
 * }
 **/
const UIMixin = Base =>
  class UI extends Base {
    constructor() {
      super();

      this.createFragmentProperty();

      let o = this;
      while ((o = Object.getPrototypeOf(o))) {
        // Selectors
        if (
          Object.prototype.hasOwnProperty.call(o.constructor, 'selectors') &&
          !Object.prototype.hasOwnProperty.call(
            o.constructor,
            '_selectorsInitialized'
          )
        ) {
          const { selectors } = o.constructor;
          Object.defineProperty(o.constructor, 'selectors', {
            value: selectors,
          });
          Object.defineProperty(o.constructor, '_selectorsInitialized', {
            value: true,
          });
        }

        // Elements
        if (
          Object.prototype.hasOwnProperty.call(o.constructor, 'elements') &&
          !Object.prototype.hasOwnProperty.call(
            o.constructor,
            '_elementsInitialized'
          )
        ) {
          const { elements } = o.constructor;
          Object.defineProperty(o.constructor, 'elements', {
            value: elements,
          });
          Object.defineProperty(o.constructor, '_elementsInitialized', {
            value: true,
          });
          for (let name in elements) {
            let selector = elements[name];
            if (selector instanceof Function) {
              o.constructor.prototype[name] = function (...args) {
                return this.querySelector(selector(...args));
              };
              continue;
            }

            assert(
              selector,
              `[ui] ${this.constructor.uiname}: no selector for ${name} element accessor`
            );

            // Property accessors.
            let isPlural = name.endsWith('s');
            Object.defineProperty(o.constructor.prototype, name, {
              get() {
                return isPlural
                  ? this.querySelectorAll(selector)
                  : this.querySelector(selector);
              },
            });
          }
        }
      }
    }

    /**
     * Activates loading state on the widget by adding a 'loading' class name
     * to |getUILoadingElement()| element.
     */
    loadingOn() {
      if (this.#loadingPromise) {
        return false;
      }

      log(`[ui] ${this.constructor.uiname} loading on`);

      let el = this.getUILoadingElement();
      assert(el, `[ui] no UI loading element`);
      assert(
        el instanceof window.Element,
        `[ui] UI loading element is not Element`
      );

      this.#loadingPromise = new AsapPromise();
      if (!this.#uiLoader) {
        this.#uiLoader = new this.constructor.UILoaderClass();
      }
      this.#uiLoader.animate(el, this.#loadingPromise, this.loadingTimeout);
      return true;
    }

    /**
     * Turns loading state off.
     */
    loadingOff() {
      if (this.#loadingPromise) {
        log(`[ui] ${this.constructor.uiname} loading off`);
        this.#loadingPromise.resolve();
        this.#loadingPromise = null;
      }
    }

    isLoading() {
      return !!this.#loadingPromise;
    }

    get loadingPromise() {
      return Promise.resolve(this.#loadingPromise);
    }

    // Returns an element for the UI widget which gets 'loading' class name on.
    // [virtual]
    getUILoadingElement() {
      return this;
    }

    // Defines a loading timeout for the element.
    // [virtual]
    loadingTimeout = UILoader.DefaultTimeout;

    // Returns a UILoader class for the given class.
    // [virtual]
    static UILoaderClass = UILoader;

    static get DefaultLoadingTimeout() {
      return UILoader.DefaultTimeout;
    }

    static get NoLoadingTimeout() {
      return UILoader.NoLoadingTimeout;
    }

    static get uiname() {
      return this.name;
    }

    createFragmentProperty(name) {
      const property = name ? `${name}Fragment` : 'fragment';
      if (!Object.prototype.hasOwnProperty.call(this.constructor, property)) {
        let markup = name ? `${name}Markup` : 'markup';
        this.constructor[property] = this.createFragment(
          this.constructor[markup]
        );

        Object.defineProperty(this.constructor.prototype, property, {
          get() {
            return window.document.importNode(this.constructor[property], true);
          },
        });
      }
    }

    createFragment(markup = '') {
      let template = window.document.createElement('template');
      template.innerHTML = markup;
      return template.content;
    }

    #loadingPromise;
    #uiLoader;
  };

const UI = UIMixin(Object);

export { UIMixin, UI };
